/* The Teams section, ported from app.ts: renderTeamsSection (~2905, the honest
   guard), renderTeams (~2757, the "no team open" pointer), renderTeamArea
   (~2778, one open team's area page). Parity with those renderers is the
   spec: same fields, same three honest states, same ARIA and keyboard, same
   CSS classNames (CONTRACT). The Roster card is the vanilla HTML
   (teams-roster.ts) rendered verbatim; everything else is JSX.

   The Allocations area and the roster's allocate/sparkline affordances went
   with allocations themselves (MVP cut, Sep 14 2026). */
import type { JSX } from "react";
import type { Team } from "../../../shared/teams";
import { badgeKey, HELP } from "../../help";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import { iPanel } from "../shell/helpText";
import { TEAM_AREAS, teamTitle } from "../shell/sections";
import { teamCardHtml } from "./teams-roster";

/* a neutral wrapper that generates no box, so an injected fragment behaves as a
   direct child of #sections (parity with the vanilla innerHTML) */
const contents = { display: "contents" } as const;

/* the list error, focusable so a rejection can be brought to the reader rather
   than left above rows they never scroll back to (listErrorLine, app.ts) */
function ListError({ error }: { error: string | null }): JSX.Element | null {
  return error ? (
    <p className="form-error" role="alert" tabIndex={-1}>
      {error}
    </p>
  ) : null;
}

/* the legacy colour-key footnotes keep their exact release copy until the
   About-Hermes feature promotes; on dev the shared key replaces them (withKey,
   app.ts). Returns HTML — badgeKey() is markup — so callers inject it. */
function withKey(aboutOn: boolean, legacyKey: string, rest: string): string {
  return aboutOn ? badgeKey() + rest : legacyKey + rest;
}

/* ---- renderTeamsSection: the two pages behind one honest guard (OH6) ---- */

export function TeamsView(): JSX.Element {
  const { state } = useAppState();
  if (state.loadError) return <LoadError what="teams" />;
  /* the server is healthy here — its teams SOURCE failed, honestly (D6/OH6) */
  if (state.teamsError)
    return (
      <LoadError
        what="teams"
        why="The teams source failed — the rest of the record is unaffected."
      />
    );
  /* D15 — an empty roster is a real finding; never claim it mid-fetch */
  if (state.teamsPending || state.loadPending) return <Loading what="teams" />;
  return state.area ? <TeamArea /> : <TeamsList />;
}

/* ---- renderTeams: no team open (D7) ---- */

function TeamsList(): JSX.Element {
  const { state, store } = useAppState();
  const n = state.teams.length;
  const footnote = withKey(
    store.on("about-hermes"),
    "<b>Red = not recorded</b> anywhere yet · <b>Orange = unconfirmed</b>. ",
    "Every value on a team carries the source and date it came from.",
  );
  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>The org at ASI.</b>
          <span>
            {n} team{n === 1 ? "" : "s"} in the Entra org chart — every value carries its source and
            date; red and orange mark the gaps. The teams intake speaks are on Teams.
          </span>
        </div>
      }
      footnote={<div style={contents} dangerouslySetInnerHTML={{ __html: footnote }} />}
    >
      <ListError error={state.listError} />
      {store.on("nav-redesign") ? (
        /* the simpler persistent list (navigation-redesign.md NR7/NR8): Teams
           has no user folders, so no rail — the section opens straight onto
           its teams, one click to open */
        <div className="team-list">
          {state.teams.map((t) => (
            <button key={t.id} className="team-row" onClick={() => store.openTeam(t.id)}>
              <span className="rec-name">{teamTitle(t)}</span>
              <span className="rec-open">Open</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="area-note">
          No team open. Choose one with <b>File → View…</b> — the picker's Teams folder lists them
          all, and its search box narrows the list as you type.
        </p>
      )}
    </ViewFrame>
  );
}

/* ---- renderTeamArea: one area page of the open team (D9) ---- */

function TeamArea(): JSX.Element {
  const { state } = useAppState();
  /* A team the source stopped returning is not open any more (N29/D7) — say so,
     exactly as a deleted project's area page does. Found by the D14 review: the
     navigator now reads the open team too, so a stale id took the whole page
     down instead of one panel. */
  const t = state.teams.find((x) => x.id === state.openTeamId);
  if (!t) {
    return (
      <ViewFrame>
        <div className="area-note">
          <p>
            That team is no longer in the record. Use <b>File → View</b> to open another.
          </p>
        </div>
      </ViewFrame>
    );
  }
  if (state.area === "Roster") return <TeamRoster t={t} />;
  return <TeamStub />;
}

/* The Roster and the stubs carry no banner (D13) — the heading above already
   names the team and area. */

/* ---- renderTeamArea's stub branch: a named area with nothing recorded ---- */

function TeamStub(): JSX.Element {
  const { state } = useAppState();
  const note = TEAM_AREAS.find((a) => a.label === state.area)?.note ?? "";
  return (
    <ViewFrame
      footnote={
        <>
          <b>Red = not recorded.</b> A named area with nothing behind it is a gap the app is honest
          about, not a page that failed to load.
        </>
      }
    >
      <ListError error={state.listError} />
      <div className="area-note">
        <p>{note}</p>
        <p>Nothing is recorded here for this team.</p>
      </div>
    </ViewFrame>
  );
}

/* ---- renderTeamArea's Roster branch + wireRosterActions ---- */

function TeamRoster({ t }: { t: Team }): JSX.Element {
  const { state, store } = useAppState();
  const panel = iPanel(state.me.surface, "stamps-and-honesty");
  const footnote = withKey(
    store.on("about-hermes"),
    "<b>Red = not recorded</b> anywhere yet · <b>Orange = unconfirmed</b>. ",
    "The roster is who reports to this manager — the manager of a team is not a member of the team they head, so their own stamp sits under the heading rather than in this list.",
  );

  return (
    <ViewFrame footnote={<div style={contents} dangerouslySetInnerHTML={{ __html: footnote }} />}>
      <ListError error={state.listError} />
      {panel && <div style={contents} dangerouslySetInnerHTML={{ __html: panel }} />}
      <div style={contents} dangerouslySetInnerHTML={{ __html: teamCardHtml(t) }} />
    </ViewFrame>
  );
}

